import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Button, Spinner, Row, Col, Label, Input } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import FeatherIcon from 'feather-icons-react';
import api from '../helpers/api';

/**
 * Quick-assign modal for a work order. Lets the caller pick any combination of
 * users and teams; the parent's `onAssign(ticketId, userIds, teamIds)` callback
 * receives both arrays. Teams are loaded from `team/all` so all teams in the
 * active org are pickable.
 */
const AssignTechnicianModal = ({
  isOpen,
  toggle,
  ticketId,
  onAssign,
  isPendingStatus,
  assignedTechnician,
  assignedTeams,
  // Legacy prop kept for compat — currently unused.
  // eslint-disable-next-line no-unused-vars
  getData,
}) => {
  const { t } = useTranslation();
  const [technicians, setTechnicians] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTechnicians, setSelectedTechnicians] = useState([]);
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [assigning, setAssigning] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [hasExistingAssignment, setHasExistingAssignment] = useState(false);
  const user = JSON.parse(localStorage.getItem('authUser') || '{}');
  const organizationId = user?.activeOrganizationId ?? null;

  useEffect(() => {
    if (!isOpen) return;

    // Initialize selected technicians (handle list, single object, single id).
    if (Array.isArray(assignedTechnician) && assignedTechnician.length > 0) {
      setSelectedTechnicians(assignedTechnician.map((tech) => tech.id || tech));
    } else if (assignedTechnician && !Array.isArray(assignedTechnician)) {
      setSelectedTechnicians([assignedTechnician.id || assignedTechnician]);
    } else {
      setSelectedTechnicians([]);
    }

    // Initialize selected teams from the WO response.
    if (Array.isArray(assignedTeams) && assignedTeams.length > 0) {
      setSelectedTeams(assignedTeams.map((tm) => tm.id || tm));
    } else {
      setSelectedTeams([]);
    }

    const hadExisting =
      (Array.isArray(assignedTechnician) && assignedTechnician.length > 0) ||
      (assignedTechnician && !Array.isArray(assignedTechnician)) ||
      (Array.isArray(assignedTeams) && assignedTeams.length > 0);
    setHasExistingAssignment(Boolean(hadExisting));

    fetchOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, assignedTechnician, assignedTeams]);

  const fetchOptions = async () => {
    try {
      setLoading(true);
      const [techRes, teamRes] = await Promise.allSettled([
        api.get(`user/technicians?organizationId=${organizationId}`),
        api.get('team/all'),
      ]);
      if (techRes.status === 'fulfilled') {
        setTechnicians(techRes.value.data?.response || []);
      } else {
        setTechnicians([]);
      }
      if (teamRes.status === 'fulfilled') {
        setTeams(teamRes.value.data?.response || []);
      } else {
        setTeams([]);
      }
    } catch (error) {
      console.error('Error loading assignee options:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (selectedTechnicians.length === 0 && selectedTeams.length === 0) return;
    try {
      setAssigning(true);
      await onAssign(ticketId, selectedTechnicians, selectedTeams);
    } finally {
      setAssigning(false);
    }
  };

  const handleRemoveAll = async () => {
    try {
      setRemoving(true);
      await onAssign(ticketId, [], []);
      toggle();
    } finally {
      setRemoving(false);
    }
  };

  const techByIdMap = useMemo(
    () => new Map(technicians.map((t2) => [t2.id, t2])),
    [technicians]
  );
  const teamByIdMap = useMemo(
    () => new Map(teams.map((tm) => [tm.id, tm])),
    [teams]
  );

  const totalSelected = selectedTechnicians.length + selectedTeams.length;

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.2 }}
      >
        <div className="modal-header">
          <h5 className="modal-title">
            <FeatherIcon icon="user-plus" className="me-2" />
            {isPendingStatus
              ? t('shared.buttons_assign_assignees_pending', 'Assign & set to Pending')
              : t('shared.buttons_assign_assignees', 'Assign')}
          </h5>
          <button
            type="button"
            className="btn-close"
            onClick={toggle}
            aria-label={t('shared.close', 'Close')}
          />
        </div>
        <div className="modal-body">
          {loading ? (
            <div className="text-center py-4">
              <Spinner color="primary" />
              <p className="mt-2">
                {t('shared.loading_assignees', 'Loading assignees...')}
              </p>
            </div>
          ) : (
            <>
              {/* Technicians */}
              <Row className="mb-3">
                <Col md={12}>
                  <Label for="technicianSelect">
                    <FeatherIcon icon="user" size={14} className="me-1" />
                    {t('shared.select_technicians', 'Select Technicians')}
                  </Label>
                  <Input
                    id="technicianSelect"
                    type="select"
                    value=""
                    onChange={(e) => {
                      if (
                        e.target.value &&
                        !selectedTechnicians.includes(parseInt(e.target.value, 10))
                      ) {
                        setSelectedTechnicians([
                          ...selectedTechnicians,
                          parseInt(e.target.value, 10),
                        ]);
                      }
                    }}
                  >
                    <option value="">
                      {t('shared.select_a_technician_to_add', 'Select a technician to add')}
                    </option>
                    {technicians
                      .filter((tech) => !selectedTechnicians.includes(tech.id))
                      .map((tech) => (
                        <option key={tech.id} value={tech.id}>
                          {tech.name}
                        </option>
                      ))}
                  </Input>
                  <div className="d-flex flex-wrap gap-2 mt-2">
                    {selectedTechnicians.map((id) => {
                      const tech = techByIdMap.get(id);
                      return (
                        <span
                          key={`u-${id}`}
                          className="badge bg-primary d-flex align-items-center gap-1"
                          style={{ fontSize: 12, padding: '6px 10px' }}
                        >
                          <FeatherIcon icon="user" size={12} />
                          {tech ? tech.name : `#${id}`}
                          <button
                            type="button"
                            className="btn-close btn-close-white ms-1"
                            style={{ fontSize: 8 }}
                            onClick={() =>
                              setSelectedTechnicians(
                                selectedTechnicians.filter((x) => x !== id)
                              )
                            }
                            aria-label={t('shared.remove', 'Remove')}
                          />
                        </span>
                      );
                    })}
                  </div>
                </Col>
              </Row>

              {/* Teams */}
              <Row className="mb-3">
                <Col md={12}>
                  <Label for="teamSelect">
                    <FeatherIcon icon="users" size={14} className="me-1" />
                    {t('teams.assignToTeams', 'Assign to Teams')}
                  </Label>
                  <Input
                    id="teamSelect"
                    type="select"
                    value=""
                    onChange={(e) => {
                      if (
                        e.target.value &&
                        !selectedTeams.includes(parseInt(e.target.value, 10))
                      ) {
                        setSelectedTeams([
                          ...selectedTeams,
                          parseInt(e.target.value, 10),
                        ]);
                      }
                    }}
                  >
                    <option value="">
                      {t('teams.selectTeamToAdd', 'Select a team to add')}
                    </option>
                    {teams
                      .filter((tm) => !selectedTeams.includes(tm.id))
                      .map((tm) => (
                        <option key={tm.id} value={tm.id}>
                          {tm.name}
                          {tm.memberCount != null
                            ? ` (${tm.memberCount} ${t('shared.members', 'members')})`
                            : ''}
                        </option>
                      ))}
                  </Input>
                  <div className="d-flex flex-wrap gap-2 mt-2">
                    {selectedTeams.map((id) => {
                      const team = teamByIdMap.get(id);
                      return (
                        <span
                          key={`t-${id}`}
                          className="badge d-flex align-items-center gap-1"
                          style={{
                            fontSize: 12,
                            padding: '6px 10px',
                            background: team?.color || '#0E7C7B',
                            color: '#fff',
                          }}
                        >
                          <FeatherIcon icon="users" size={12} />
                          {team ? team.name : `#${id}`}
                          <button
                            type="button"
                            className="btn-close btn-close-white ms-1"
                            style={{ fontSize: 8 }}
                            onClick={() =>
                              setSelectedTeams(selectedTeams.filter((x) => x !== id))
                            }
                            aria-label={t('shared.remove', 'Remove')}
                          />
                        </span>
                      );
                    })}
                  </div>
                  <small className="text-muted d-block mt-1">
                    {t(
                      'teams.assignNotice',
                      'All members of selected teams will be notified.'
                    )}
                  </small>
                </Col>
              </Row>

              {totalSelected === 0 && (
                <small className="text-muted">
                  {t('shared.no_assignees_selected', 'No assignees selected')}
                </small>
              )}

              {isPendingStatus && (
                <div className="alert alert-info mb-0 mt-3">
                  <FeatherIcon icon="info" className="me-2" />
                  {t(
                    'shared.assign_pending_notice',
                    'The ticket status will be changed to "Pending" when you assign.'
                  )}
                </div>
              )}
            </>
          )}
        </div>
        <div className="modal-footer d-flex justify-content-between">
          <div>
            {hasExistingAssignment && (
              <Button
                color="danger"
                outline
                onClick={handleRemoveAll}
                disabled={removing || assigning}
              >
                {removing ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    {t('shared.removing', 'Removing...')}
                  </>
                ) : (
                  <>
                    <FeatherIcon icon="user-minus" size={14} className="me-1" />
                    {t('shared.button_remove_all', 'Remove All')}
                  </>
                )}
              </Button>
            )}
          </div>
          <div className="d-flex gap-2">
            <Button color="secondary" onClick={toggle}>
              {t('shared.cancel', 'Cancel')}
            </Button>
            <Button
              color="primary"
              onClick={handleSave}
              disabled={totalSelected === 0 || assigning || removing}
            >
              {assigning ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  {t('shared.assigning', 'Assigning...')}
                </>
              ) : (
                `${t('shared.assign', 'Assign')} (${totalSelected})`
              )}
            </Button>
          </div>
        </div>
      </motion.div>
    </Modal>
  );
};

export default AssignTechnicianModal;
